import { useCallback, useMemo, useRef, useState } from 'react';

export type ListFilterOption = { label: string; value: string };

interface UseListViewFiltersProps<K extends string> {
  initialSelectedByColumn: Record<K, string[]>;
  initialSearchByColumn?: Partial<Record<K, string>>;
}

function cloneSelectedByColumn<K extends string>(selectedByColumn: Record<K, string[]>) {
  const nextSelectedByColumn = {} as Record<K, string[]>;
  const keys = Object.keys(selectedByColumn) as K[];

  keys.forEach((key) => {
    nextSelectedByColumn[key] = [...selectedByColumn[key]];
  });

  return nextSelectedByColumn;
}

export default function useListViewFilters<K extends string>(props: UseListViewFiltersProps<K>) {
  const initialSelectedByColumnRef = useRef(cloneSelectedByColumn(props.initialSelectedByColumn));
  const initialSearchByColumnRef = useRef({ ...(props.initialSearchByColumn || {}) });

  const [selectedByColumn, setSelectedByColumn] = useState<Record<K, string[]>>(() =>
    cloneSelectedByColumn(initialSelectedByColumnRef.current)
  );
  const [searchByColumn, setSearchByColumn] = useState<Partial<Record<K, string>>>(() => ({
    ...initialSearchByColumnRef.current,
  }));

  const toggleValue = useCallback((key: K, value: string) => {
    setSelectedByColumn((prev) => {
      const isActive = prev[key].includes(value);
      const nextValues = isActive ? prev[key].filter((selectedValue) => selectedValue !== value) : [...prev[key], value];
      return { ...prev, [key]: nextValues };
    });
  }, []);

  const setOptionSearch = useCallback((key: K, value: string) => {
    setSearchByColumn((prev) => ({
      ...prev,
      [key]: value,
    }));
  }, []);

  const clearAll = useCallback(() => {
    setSelectedByColumn(cloneSelectedByColumn(initialSelectedByColumnRef.current));
    setSearchByColumn({ ...initialSearchByColumnRef.current });
  }, []);

  const activeCount = useMemo(
    () => (Object.values(selectedByColumn) as string[][]).reduce((count, values) => count + values.length, 0),
    [selectedByColumn]
  );
  const hasFilters = activeCount > 0;

  const applyFilters = useCallback(
    <T>(items: T[], getValueForColumn: (item: T, key: K) => string) => {
      const keys = Object.keys(selectedByColumn) as K[];

      return items.filter((item) => {
        for (const key of keys) {
          const selectedValues = selectedByColumn[key];
          if (selectedValues.length === 0) continue;

          const itemValue = `${getValueForColumn(item, key) ?? ''}`.trim();
          if (!selectedValues.includes(itemValue)) return false;
        }

        return true;
      });
    },
    [selectedByColumn]
  );

  const buildFilterOptions = useCallback((values: (string | number | undefined)[]): ListFilterOption[] => {
    return Array.from(new Set(values.map((value) => `${value ?? ''}`.trim()).filter((value) => value.length > 0)))
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ label: value, value }));
  }, []);

  return {
    selectedByColumn,
    searchByColumn,
    toggleValue,
    setOptionSearch,
    clearAll,
    activeCount,
    hasFilters,
    applyFilters,
    buildFilterOptions,
  };
}
